export enum USER_ROLES_VALUES {
  user       = 'user',
  admin      = 'ADMIN',
  superAdmin = 'SUPERADMIN'
}

export interface IMeResponse {
  userId: string,
  accountId: string | null;
  email: string,
  fullname: string,

  role: USER_ROLES_VALUES,
  permissions: PermissionMap,
  settings: USER_SETTINGS_VALUES[];
}

export interface IUser {
  username: string;
  name: string;
  _id: string;
  role: string;
  phone: string;
  website: string;
  settings: string[];
  last_login: number;
}
export interface IUserWithStats extends IUser {
  stats: {
    ads_impression_last_month: number;
    ads_impression_current_month: number;
  };
}

export enum ACCOUNT_PERMISSION_MODE_VALUES {
  ENABLED  = '1',
  DISABLED = '0',
}

export class Me implements IMeResponse {
  public accountId: string | null;
  public email: string;
  public fullname: string;
  public permissions: PermissionMap;
  public role: USER_ROLES_VALUES;
  public settings: USER_SETTINGS_VALUES[];
  public userId: string;

  public hasPermission(permission: ACCOUNT_PERMISSIONS) {
    if (!this.accountId) {
      //no account id -> main account;
      return true;
    }
    return this.permissions[permission] === ACCOUNT_PERMISSION_MODE_VALUES.ENABLED;
  }

  public isAdmin() {
    return this.role === USER_ROLES_VALUES.admin;
  }

  public isSuperAdmin() {
    return this.role === USER_ROLES_VALUES.superAdmin;
  }

  public hasSetting(setting: USER_SETTINGS_VALUES) {
    return this.settings.indexOf(setting) !== -1;
  }

  constructor(data?: IMeResponse) {
    data && Object.assign(this, data);
  }

}

export class UserModel {
  email: string;
  name: string;
  account_id: string;
  user_id: string;
  role: USER_ROLES_VALUES = USER_ROLES_VALUES.user;
  settings: USER_SETTINGS_VALUES[] = [];
  permissions: PermissionMap = {};

  constructor(data?: IMeResponse) {
    data && Object.assign(this, data);
  }

  public isAdmin() {
    return this.role === USER_ROLES_VALUES.admin;
  }

  public isSuperAdmin() {
    return this.role === USER_ROLES_VALUES.superAdmin;
  }

  public hasSetting(setting: USER_SETTINGS_VALUES) {
    return this.settings.indexOf(setting) !== -1;
  }
}

export const USERS_PAGE_MODE = {
  ADD:  '1',
  EDIT: '2',
};

export const USER_ROLES = {
  [USER_ROLES_VALUES.superAdmin]: 'Super admin',
  [USER_ROLES_VALUES.admin]:      'Admin',
  [USER_ROLES_VALUES.user]:       'Client',
};

//main users
export enum USER_STATUS_VALUES {
  ENABLED  = '1',
  DISABLED = '2',
}

export const USER_STATUS = {
  [USER_STATUS_VALUES.DISABLED]: 'Disabled',
  [USER_STATUS_VALUES.ENABLED]:  'Enabled',
};

export enum USER_SETTINGS_VALUES {
  CAN_UPLOAD_VIDEO_POSTER      = 'can_upload_video_poster',
  VIDEO_3G_MAX_SIZE            = 'video_3gb',
  VIDEO_200MB_MAX_SIZE         = 'video_200mb',
  VIDEO_NO_SIZE_LIMIT          = 'video_no_size_limit',
  YB_SCRIPTS                   = 'yb_scripts',
  PRO_ANALYTIC                 = 'pro_analytic',
  PLAYER_CONTEXT_MENU_DISABLED = 'player_context_menu_disabled',
  PLAYER_ENABLE_WHITELABEL     = 'player_enable_whitelabel',
  PLAYER_EVENT_DISPATCHER      = 'player_event_dispatcher_enabled',
}

export enum ACCOUNT_SETTINGS_VALUES {
  DAILY_ANALYTIC_EMAIL = 'daily_analytic_email',
}

export const USER_SETTINGS = {
  [USER_SETTINGS_VALUES.CAN_UPLOAD_VIDEO_POSTER]:      'Can upload video poster',
  [USER_SETTINGS_VALUES.VIDEO_NO_SIZE_LIMIT]:          'Video no limit maximum size',
  [USER_SETTINGS_VALUES.VIDEO_200MB_MAX_SIZE]:         'Video Maximum size: 200MB',
  [USER_SETTINGS_VALUES.VIDEO_3G_MAX_SIZE]:            'Video Maximum size: 3G',
  [USER_SETTINGS_VALUES.YB_SCRIPTS]:                   'Yieldbird scripts (e.g. Price genius)',
  [USER_SETTINGS_VALUES.PRO_ANALYTIC]:                 'Pro analytics (e.g. show ad errors)',
  [USER_SETTINGS_VALUES.PLAYER_CONTEXT_MENU_DISABLED]: 'Disable Veedmo branding context menu on player',
  [USER_SETTINGS_VALUES.PLAYER_ENABLE_WHITELABEL]:     'Enable whitelabel for player',
  [USER_SETTINGS_VALUES.PLAYER_EVENT_DISPATCHER]:      'Enable player event dispatcher',
};

export class User implements IUser {
  public _id: string;
  public role: string;
  public username: string;
  public name: string;
  public phone: string;
  public website: string;
  public settings: string[];
  public last_login: number;
  public status: string;

  constructor(user?: any) {
    user && Object.assign(this, user);
  }
}

export interface IAccount {
  _id: string;
  user_id: string;
  name: string;
  email: string;
  password: string;
  status: string;
  permissions: PermissionMap;
  last_login: number;
}

export class Account implements IAccount {
  public _id: string;
  public user_id: string;
  public name: string;
  public email: string;
  public password: string;
  public status: string = USER_STATUS_VALUES.ENABLED;
  public permissions: PermissionMap = initialAccountPermission;
  public last_login: number;

  constructor(account?: any) {
    account && Object.assign(this, account);
  }
}

export type PermissionMap = Partial<
  Record<ACCOUNT_PERMISSIONS, ACCOUNT_PERMISSION_MODE_VALUES>
>;

export type AccountPermissionTypes = 'stats_read' | 'media_read' | 'media_write' | 'media_delete'
  | 'players_read' | 'players_write' | 'players_delete' | 'ads_read' | 'ads_write'
  | 'ads_delete' | 'embeds_read' | 'embeds_write' | 'embeds_delete' | 'accounts_read' | 'accounts_write' | 'accounts_delete';

export enum ACCOUNT_PERMISSIONS {
  stats_read      = 'stats_read',
  media_read      = 'media_read',
  media_write     = 'media_write',
  media_delete    = 'media_delete',
  players_read    = 'players_read',
  players_write   = 'players_write',
  players_delete  = 'players_delete',
  ads_read        = 'ads_read',
  ads_write       = 'ads_write',
  ads_delete      = 'ads_delete',
  embeds_read     = 'embeds_read',
  embeds_write    = 'embeds_write',
  embeds_delete   = 'embeds_delete',
  accounts_read   = 'accounts_read',
  accounts_write  = 'accounts_write',
  accounts_delete = 'accounts_delete',
}

export const initialAccountPermission: PermissionMap = {
  [ACCOUNT_PERMISSIONS.stats_read]:      ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.media_read]:      ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.media_write]:     ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.media_delete]:    ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.players_read]:    ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.players_write]:   ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.players_delete]:  ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.ads_read]:        ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.ads_write]:       ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.ads_delete]:      ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.embeds_read]:     ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.embeds_write]:    ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.embeds_delete]:   ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.accounts_read]:   ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.accounts_write]:  ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
  [ACCOUNT_PERMISSIONS.accounts_delete]: ACCOUNT_PERMISSION_MODE_VALUES.ENABLED,
};

export const AccountPermissionTree: { [key: string]: Partial<AccountPermissionTypes>[]; } = {
  'permission_group_stats':    [
    'stats_read',
  ],
  'permission_group_media':    [
    'media_read', 'media_write', 'media_delete',
  ],
  'permission_group_players':  [
    'players_read', 'players_write', 'players_delete',
  ],
  'permission_group_ads':      [
    'ads_read', 'ads_write', 'ads_delete',
  ],
  'permission_group_embeds':   [
    'embeds_read', 'embeds_write', 'embeds_delete',
  ],
  'permission_group_accounts': [
    'accounts_read', 'accounts_write', 'accounts_delete',
  ],
};

export const checkAccountPermission = (account: IAccount, permissionName: AccountPermissionTypes): boolean => {

  if (!account || !account.permissions || !account.permissions[permissionName]) {
    return false;
  }
  return account.permissions[permissionName] === ACCOUNT_PERMISSION_MODE_VALUES.ENABLED;
};

export const ACCOUNT_STATUS = {
  [USER_STATUS_VALUES.ENABLED]:  'status_active',
  [USER_STATUS_VALUES.DISABLED]: 'status_inactive',
};
